import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Download, Copy, Spinner, FileText, FilmSlate, ArrowSquareOut,
  Globe, ListChecks, CurrencyDollar, EnvelopeSimple, Eye, Trash,
  Lock,
} from '@phosphor-icons/react';
import { creationsAPI } from '@/utils/api';

/**
 * Round 58/59 — Permanent source-of-truth detail view.
 *
 * Opens the COMPLETE saved creation (sales page, ebook, offer, lead
 * magnet, email sequence, DFY video, content pack) and offers all
 * relevant downloads — PDF, HTML, video — with NO regeneration
 * required.
 *
 * Round 59 — Customer ownership actions:
 *   • View / Copy / Download / Delete on every project.
 *   • Tier-aware: starter sees copy/text only; growth gets PDF+HTML;
 *     pro gets the full DFY package. Backend enforces gating based
 *     on tier_at_save (ownership preserved on downgrade).
 *
 * Props
 * -----
 * creationId  string         — pass `null` to close
 * onClose     () => void
 * onDeleted   (id) => void   — fired after a successful delete so the
 *                              parent list can drop the row
 */
const CreationDetailModal = ({ creationId, onClose, onDeleted }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!data?.id) return;
    const ok = window.confirm(
      `Delete "${data.title || 'this asset'}"? This permanently removes it from My Projects and cannot be undone.`,
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await creationsAPI.delete(data.id);
      toast.success('Asset deleted.');
      if (onDeleted) onDeleted(data.id);
      onClose?.();
    } catch (err) {
      console.error('delete failed:', err);
      toast.error(err?.response?.data?.detail || 'Could not delete the asset.');
    } finally {
      setDeleting(false);
    }
  };

  const fetchFull = useCallback(async (id) => {
    setLoading(true);
    setError('');
    try {
      const res = await creationsAPI.getFull(id, true);
      setData(res?.data || null);
    } catch (err) {
      console.error('reopen failed:', err);
      setError(err?.response?.data?.detail || 'Could not load the asset.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!creationId) return undefined;
    let cancelled = false;
    // Defer the state-setting call to a microtask so the
    // react-hooks/set-state-in-effect linter doesn't flag it.
    Promise.resolve().then(() => {
      if (!cancelled) fetchFull(creationId);
    });
    return () => { cancelled = true; };
  }, [creationId, fetchFull]);

  if (!creationId) return null;

  const safe = (v, max = 8000) => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.slice(0, max);
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (Array.isArray(v)) return v.map((x) => safe(x, 200)).filter(Boolean).join('\n• ');
    if (typeof v === 'object') {
      const k = ['title', 'name', 'headline', 'text', 'label'].find((x) => v[x]);
      return k ? String(v[k]).slice(0, max) : '';
    }
    return String(v).slice(0, max);
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error('Could not copy — please select manually.');
    }
  };

  const downloadPdf = () => {
    if (!data?.exports?.pdf_base64) return;
    try {
      const byteCharacters = atob(data.exports.pdf_base64);
      const bytes = new Uint8Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) bytes[i] = byteCharacters.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.exports.pdf_filename || `${data.title || 'asset'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded.');
    } catch (err) {
      console.error('pdf download failed:', err);
      toast.error('PDF download failed.');
    }
  };

  const downloadHtml = () => {
    if (!data?.exports?.html) return;
    const blob = new Blob([data.exports.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.title || 'asset'}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('HTML downloaded.');
  };

  // ─── per-type renderers (data.content is the FULL original payload) ───
  // Round 61 — when the saved row carries `full_page_html`, the
  // modal renders that finished deliverable in an iframe instead of
  // a list of source-field cards. The Copy / per-field cards stay
  // available below as a secondary surface for tier-restricted
  // copy-only users. Sales pages always preview the styled page.
  const finishedHtml =
    typeof (data?.exports?.html) === 'string' && data.exports.html.trim().length > 0
      ? data.exports.html
      : (typeof (data?.content?.full_page_html) === 'string' && data.content.full_page_html.trim().length > 0
          ? data.content.full_page_html
          : '');

  const renderContent = () => {
    if (!data) return null;
    const c = data.content || {};
    const t = data.type || '';

    if (t === 'sales_page') return renderSalesPage(c, safe, copy);
    if (t === 'ebook') return renderEbook(c, safe, copy);
    if (t === 'offer' || t === 'offer_sheet') return renderOffer(c, safe, copy);
    if (t === 'lead_magnet') return renderLeadMagnet(c, safe, copy);
    if (t === 'email_sequence') return renderEmailSeq(c, safe, copy);
    if (t === 'content_pack') return renderContentPack(c, safe, copy);
    if (t === 'business_plan') return renderBusinessPlan(c, safe, copy);
    if (t === 'dfy_video') return renderDfyVideo(data, safe);
    if (t === 'business_package') return renderBusinessPackage(data, safe);

    // Generic — render every primitive field with a copy button.
    return (
      <div className="space-y-3" data-testid="creation-detail-generic">
        {Object.entries(c).map(([k, v]) => {
          if (v === null || v === undefined || v === '') return null;
          return (
            <FieldCard key={k} label={k.replace(/_/g, ' ')} value={safe(v)} onCopy={() => copy(safe(v), k)} />
          );
        })}
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="creation-detail-overlay"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 20, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl bg-slate-950 border border-white/10 shadow-2xl"
          data-testid="creation-detail-modal"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-950/95 backdrop-blur-md border-b border-white/10 px-5 py-4 flex items-center justify-between gap-3 z-10">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-amber-300">
                <span data-testid="creation-detail-type">{(data?.type || '').replace(/_/g, ' ')}</span>
                {data?.tier_at_save && (
                  <span className="text-slate-400" data-testid="creation-detail-tier">
                    · {data.tier_at_save} tier
                  </span>
                )}
                {data?.is_legacy && (
                  <span className="text-rose-300" data-testid="creation-detail-legacy">· legacy</span>
                )}
              </div>
              <h2 className="text-white text-lg sm:text-xl font-bold truncate" data-testid="creation-detail-title">
                {data?.title || (loading ? 'Loading deliverable…' : 'Untitled deliverable')}
              </h2>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleDelete}
                disabled={deleting || !data?.id}
                className="rounded-lg p-2 hover:bg-rose-500/15 text-rose-300 hover:text-rose-200 disabled:opacity-40"
                data-testid="creation-detail-delete"
                title="Delete"
                aria-label="Delete asset"
              >
                {deleting ? <Spinner size={16} className="animate-spin" /> : <Trash size={16} />}
              </button>
              <button
                onClick={onClose}
                className="rounded-lg p-2 hover:bg-white/10"
                data-testid="creation-detail-close"
                aria-label="Close"
              >
                <X size={18} className="text-white" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-4">
            {loading && (
              <div className="flex items-center justify-center py-10 text-slate-400" data-testid="creation-detail-loading">
                <Spinner size={20} className="animate-spin mr-2" /> Loading full asset…
              </div>
            )}
            {error && (
              <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-rose-200 text-sm" data-testid="creation-detail-error">
                {error}
              </div>
            )}
            {!loading && !error && data && (
              <>
                {/* Round 59 — tier-locked notice. Backend strips
                    PDF/HTML/ZIP from exports based on tier_at_save
                    + current tier, and adds tier_locked + upgrade_hint
                    so the modal can tell the user what's gated. */}
                {data.exports?.tier_locked && data.exports?.upgrade_hint && (
                  <div
                    className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 flex items-start gap-2"
                    data-testid="creation-detail-tier-locked"
                  >
                    <Lock size={16} className="text-amber-300 mt-0.5 shrink-0" />
                    <p className="text-sm text-amber-200">
                      {data.exports.upgrade_hint}
                    </p>
                  </div>
                )}

                {/* Download bar */}
                <div className="flex flex-wrap gap-2" data-testid="creation-detail-downloads">
                  {data.exports?.pdf_base64 && (
                    <button
                      onClick={downloadPdf}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                      data-testid="creation-detail-download-pdf"
                    >
                      <Download size={14} /> Download PDF
                    </button>
                  )}
                  {data.exports?.html && (
                    <button
                      onClick={downloadHtml}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                      data-testid="creation-detail-download-html"
                    >
                      <Globe size={14} /> Download HTML
                    </button>
                  )}
                  {data.exports?.video_url && (
                    <a
                      href={data.exports.video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold"
                      data-testid="creation-detail-download-video"
                    >
                      <FilmSlate size={14} /> Open Video
                    </a>
                  )}
                  {data.exports?.html && (
                    <button
                      onClick={() => {
                        const w = window.open('', '_blank');
                        if (w && data.exports?.html) {
                          w.document.open();
                          w.document.write(data.exports.html);
                          w.document.close();
                        }
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-white text-sm font-semibold"
                      data-testid="creation-detail-preview-html"
                    >
                      <Eye size={14} /> Preview
                    </button>
                  )}
                </div>

                {/* Round 61 — finished deliverable preview. When the
                    saved asset has rendered HTML, show that as the
                    PRIMARY view so My Projects feels like a
                    file-library, not a data dump. The per-type
                    field cards below remain for copy/audit. */}
                {finishedHtml && (
                  <div
                    className="rounded-xl border border-white/10 overflow-hidden bg-white"
                    data-testid="creation-detail-finished-preview"
                  >
                    <iframe
                      title="Finished deliverable"
                      srcDoc={finishedHtml}
                      sandbox="allow-same-origin"
                      className="w-full h-[60vh] sm:h-[75vh] bg-white"
                    />
                  </div>
                )}

                {/* Round 67 — when the styled deliverable is the
                    primary surface, demote the per-type field
                    cards to a collapsed <details> so customers see
                    the polished asset first and can still copy
                    individual fields on demand. When there is no
                    styled HTML (e.g. content_pack, business_plan,
                    DFY video), keep the field cards visible as the
                    primary view — they are the asset for those types. */}
                {finishedHtml ? (
                  <details
                    className="rounded-xl border border-white/10 bg-white/[0.02]"
                    data-testid="creation-detail-fields-collapsible"
                  >
                    <summary
                      className="cursor-pointer select-none px-4 py-3 text-sm text-slate-300 hover:text-white flex items-center gap-2"
                      data-testid="creation-detail-fields-summary"
                    >
                      <ListChecks size={16} />
                      <span>Source fields & quick copy</span>
                    </summary>
                    <div className="px-4 pb-4 pt-1">
                      {renderContent()}
                    </div>
                  </details>
                ) : (
                  renderContent()
                )}
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────
// Helpers + per-type renderers
// ─────────────────────────────────────────────────────────────
const FieldCard = ({ label, value, onCopy, testid }) => (
  <div
    className="rounded-lg border border-white/10 bg-white/5 p-3"
    data-testid={testid || `creation-detail-field-${label.toLowerCase().replace(/\s+/g, '_')}`}
  >
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      {onCopy && (
        <button onClick={onCopy} className="text-[11px] text-slate-300 hover:text-white inline-flex items-center gap-1">
          <Copy size={11} /> Copy
        </button>
      )}
    </div>
    <p className="text-sm text-white whitespace-pre-wrap leading-relaxed">{value || '—'}</p>
  </div>
);

// Round 60 — sales-page list-of-dict normaliser. Shared with
// MyCreationsPage SalesPageCard so both surfaces convert
// List[{benefit,description}] / List[{question,answer}] /
// List[{bonus,description}] to a readable bulleted string instead
// of crashing React with "Objects are not valid as a React child".
function _spField(value, dictKeys = ['title', 'name', 'text', 'description']) {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((it) => {
        if (it === null || it === undefined) return '';
        if (typeof it === 'string' || typeof it === 'number') return `• ${it}`;
        if (typeof it === 'object') {
          const primary = dictKeys.map((k) => it[k]).find((v) => typeof v === 'string' && v) || '';
          const usedKey = dictKeys.find((k) => it[k] === primary);
          const secondary = dictKeys
            .filter((k) => k !== usedKey)
            .map((k) => it[k])
            .find((v) => typeof v === 'string' && v) || '';
          if (primary && secondary) return `• ${primary} — ${secondary}`;
          if (primary) return `• ${primary}`;
          const anyStr = Object.values(it).find((v) => typeof v === 'string' && v) || '';
          return anyStr ? `• ${anyStr}` : '';
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  if (typeof value === 'object') {
    const k = dictKeys.find((x) => typeof value[x] === 'string' && value[x]);
    return k ? value[k] : '';
  }
  return String(value);
}

function renderSalesPage(c, safe, copy) {
  // Round 60 — render the actual saved schema. Live data inspection
  // showed sales-page rows ship with:
  //   headline, subheadline, problem_section, solution_section,
  //   benefits_section, offer_section, bonuses_section,
  //   cta_section, faq_suggestions, full_page_html.
  // Earlier renderer only knew about a generic `body` field → user
  // saw "section outlines" because 8/10 sections silently dropped.
  //
  // The on-demand styled PDF / HTML download bar is unaffected
  // (pdf_service.generate_sales_page_pdf rebuilds from these same
  // fields, and CreationDetailModal already surfaces full_page_html
  // via exports.html when present).
  const fields = [
    ['Headline', c.headline],
    ['Subheadline', c.subheadline || c.hook],
    ['Problem', _spField(c.problem_section || c.problem)],
    ['Solution', _spField(c.solution_section || c.solution)],
    ['Benefits', _spField(c.benefits_section || c.benefits, ['benefit', 'description'])],
    ['Offer', _spField(c.offer_section || c.offer_breakdown || c.what_you_get)],
    ['Bonuses', _spField(c.bonuses_section || c.bonus_section || c.bonuses, ['bonus', 'description'])],
    ['Pricing', _spField(c.pricing || c.price)],
    ['Guarantee', _spField(c.guarantee)],
    ['Call to action', _spField(c.cta_section || c.cta)],
    ['FAQ', _spField(c.faq_suggestions || c.faq, ['question', 'answer'])],
    ['Link', c.link || c.url],
  ];
  return (
    <div className="space-y-3" data-testid="creation-detail-sales_page">
      {fields.map(([label, v]) => v && (
        <FieldCard
          key={label}
          label={label}
          value={safe(v)}
          onCopy={() => copy(safe(v), label)}
        />
      ))}
    </div>
  );
}

function renderEbook(c, safe, copy) {
  return (
    <div className="space-y-3" data-testid="creation-detail-ebook">
      <FieldCard label="Title" value={safe(c.title || c.headline)} onCopy={() => copy(safe(c.title), 'Title')} />
      <FieldCard label="Subtitle" value={safe(c.subtitle || c.subheadline)} onCopy={() => copy(safe(c.subtitle), 'Subtitle')} />
      {c.cover_image_url && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3" data-testid="creation-detail-field-cover">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Cover</p>
          <img src={c.cover_image_url} alt="Cover" className="rounded-md max-h-64 mx-auto" />
        </div>
      )}
      {(c.chapters || []).map((chap, i) => (
        <FieldCard
          key={i}
          label={`Chapter ${i + 1}${chap.title ? ' — ' + safe(chap.title, 80) : ''}`}
          value={safe(chap.content || chap.body || chap)}
          onCopy={() => copy(safe(chap.content || chap.body || chap), `Chapter ${i + 1}`)}
        />
      ))}
      {c.full_text && (
        <FieldCard label="Full text" value={safe(c.full_text)} onCopy={() => copy(safe(c.full_text), 'Full text')} />
      )}
    </div>
  );
}

function renderOffer(c, safe, copy) {
  const fields = [
    ['Offer name', c.offer_name || c.name || c.title],
    ['Pitch', c.pitch || c.description],
    ['Benefits', c.benefits],
    ['Pricing', c.pricing || c.price],
    ['Bonuses', c.bonuses],
    ['Guarantee', c.guarantee],
    ['CTA', c.cta],
  ];
  return (
    <div className="space-y-3" data-testid="creation-detail-offer">
      {fields.map(([label, v]) => v && (
        <FieldCard key={label} label={label} value={safe(v)} onCopy={() => copy(safe(v), label)} />
      ))}
    </div>
  );
}

function renderLeadMagnet(c, safe, copy) {
  const fields = [
    ['Title', c.title || c.name],
    ['Subheadline', c.subheadline],
    ['Body', c.body || c.content || c.full_text],
    ['Suggested social post', c.suggested_social_post || c.social_post],
    ['CTA', c.cta || c.cta_copy],
    ['Recommended platform', c.recommended_platform],
  ];
  return (
    <div className="space-y-3" data-testid="creation-detail-lead_magnet">
      {fields.map(([label, v]) => v && (
        <FieldCard key={label} label={label} value={safe(v)} onCopy={() => copy(safe(v), label)} />
      ))}
    </div>
  );
}

function renderEmailSeq(c, safe, copy) {
  const emails = c.emails || c.steps || [];
  return (
    <div className="space-y-3" data-testid="creation-detail-email_sequence">
      {emails.map((e, i) => (
        <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-3" data-testid={`creation-detail-email-${i}`}>
          <p className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold mb-1">Email {i + 1}</p>
          <FieldCard label="Subject" value={safe(e.subject)} onCopy={() => copy(safe(e.subject), `Email ${i + 1} subject`)} />
          <FieldCard label="Body" value={safe(e.body || e.content)} onCopy={() => copy(safe(e.body || e.content), `Email ${i + 1} body`)} />
          {e.cta && <FieldCard label="CTA" value={safe(e.cta)} onCopy={() => copy(safe(e.cta), `Email ${i + 1} CTA`)} />}
        </div>
      ))}
      {!emails.length && c.body && (
        <FieldCard label="Full sequence" value={safe(c.body)} onCopy={() => copy(safe(c.body), 'Full sequence')} />
      )}
    </div>
  );
}

function renderContentPack(c, safe, copy) {
  const fields = [
    ['Hook', c.hook],
    ['Voice script', c.voice_script],
    ['Video script', c.video_script],
    ['Caption', c.caption || c.body],
    ['Hashtags', c.hashtags],
    ['CTA', c.cta],
    ['Video prompt', c.video_prompt],
    ['Voice prompt', c.voice_prompt],
    ['Platform', c.platform],
  ];
  return (
    <div className="space-y-3" data-testid="creation-detail-content_pack">
      {fields.map(([label, v]) => v && (
        <FieldCard key={label} label={label} value={safe(v)} onCopy={() => copy(safe(v), label)} />
      ))}
    </div>
  );
}

function renderBusinessPlan(c, safe, copy) {
  return (
    <div className="space-y-3" data-testid="creation-detail-business_plan">
      {Object.entries(c).map(([k, v]) => {
        if (!v) return null;
        return <FieldCard key={k} label={k.replace(/_/g, ' ')} value={safe(v)} onCopy={() => copy(safe(v), k)} />;
      })}
    </div>
  );
}

function renderBusinessPackage(data, safe) {
  const c = data.content || {};
  const files = c.files || data.exports?.files || [];
  return (
    <div className="space-y-3" data-testid="creation-detail-business_package">
      {c.business_type && (
        <FieldCard label="Business type" value={safe(c.business_type)} />
      )}
      {data.exports?.download_url && (
        <div
          className="rounded-lg border border-violet-500/30 bg-violet-500/10 p-3"
          data-testid="creation-detail-package-download"
        >
          <p className="text-[10px] uppercase tracking-wider text-violet-300 font-semibold mb-2">
            Full package
          </p>
          <a
            href={data.exports.download_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold"
          >
            <Download size={14} /> Download ZIP
          </a>
        </div>
      )}
      {files.length > 0 && (
        <div
          className="rounded-lg border border-white/10 bg-white/5 p-3"
          data-testid="creation-detail-package-files"
        >
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">
            Included files
          </p>
          <ul className="space-y-1">
            {files.map((f, i) => (
              <li key={i} className="text-sm text-white flex items-center gap-2">
                <FileText size={14} className="text-slate-400" />
                {typeof f === 'string' ? f : safe(f, 200)}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function renderDfyVideo(data, safe) {
  return (
    <div className="space-y-3" data-testid="creation-detail-dfy_video">
      {data.exports?.video_url && (
        <video
          src={data.exports.video_url}
          controls
          className="w-full rounded-md bg-black max-h-[60vh]"
          data-testid="creation-detail-video-player"
        />
      )}
      {data.content?.prompt && (
        <FieldCard label="Prompt" value={safe(data.content.prompt)} />
      )}
    </div>
  );
}

export default CreationDetailModal;
